import { useEffect, useRef, useState } from "react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.ringoi.com";
const SHOW_DELAY_MS = 180;
const MIN_VISIBLE_MS = 260;

function isCrmRequest(url) {
  try {
    const target = new URL(String(url || ""), window.location.href);
    const api = new URL(API_BASE_URL, window.location.href);
    return target.origin === window.location.origin || target.origin === api.origin;
  } catch {
    return false;
  }
}

/**
 * A small, global request-progress indicator for the CRM workspace.  It wraps
 * only browser fetch/XHR calls to the CRM or its API, leaving third-party
 * assets alone.  The indicator is delayed so successful quick operations stay
 * quiet, while longer reports and saves give the agent clear feedback.
 */
export default function NetworkProgress() {
  const activeCountRef = useRef(0);
  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const visibleSinceRef = useRef(0);
  const visibleRef = useRef(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const clearTimers = () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      showTimerRef.current = null;
      hideTimerRef.current = null;
    };

    const begin = () => {
      activeCountRef.current += 1;
      if (visibleRef.current || showTimerRef.current) return;
      showTimerRef.current = window.setTimeout(() => {
        showTimerRef.current = null;
        if (activeCountRef.current > 0) {
          visibleSinceRef.current = Date.now();
          visibleRef.current = true;
          setVisible(true);
        }
      }, SHOW_DELAY_MS);
    };

    const finish = () => {
      activeCountRef.current = Math.max(0, activeCountRef.current - 1);
      if (activeCountRef.current > 0) return;

      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      if (!visibleRef.current) return;

      const remaining = Math.max(
        0,
        MIN_VISIBLE_MS - (Date.now() - visibleSinceRef.current),
      );
      hideTimerRef.current = window.setTimeout(() => {
        hideTimerRef.current = null;
        if (activeCountRef.current === 0) {
          visibleRef.current = false;
          setVisible(false);
        }
      }, remaining);
    };

    const originalFetch = window.fetch;
    const originalXhrSend = XMLHttpRequest.prototype.send;
    const originalXhrOpen = XMLHttpRequest.prototype.open;

    window.fetch = function trackedFetch(input, init) {
      const url = typeof input === "string" ? input : input?.url;
      if (!isCrmRequest(url)) return originalFetch.call(this, input, init);
      begin();
      try {
        return originalFetch.call(this, input, init).finally(finish);
      } catch (error) {
        finish();
        throw error;
      }
    };

    XMLHttpRequest.prototype.open = function trackedOpen(method, url, ...rest) {
      this.__crmProgressRequest = isCrmRequest(url);
      return originalXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function trackedSend(...args) {
      if (!this.__crmProgressRequest) {
        return originalXhrSend.apply(this, args);
      }

      begin();
      this.addEventListener("loadend", finish, { once: true });
      try {
        return originalXhrSend.apply(this, args);
      } catch (error) {
        finish();
        throw error;
      }
    };

    return () => {
      clearTimers();
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.send = originalXhrSend;
      XMLHttpRequest.prototype.open = originalXhrOpen;
    };
  }, []);

  return (
    <div
      className={`app-network-progress${visible ? " is-visible" : ""}`}
      aria-live="polite"
      aria-label={visible ? "Loading the latest CRM data" : undefined}
      aria-hidden={!visible}
    >
      <span className="app-network-progress__bar" />
    </div>
  );
}
